// Custom (kernel) — user-defined convolution kernel.
// Functional approximation: applies a user-specified N×N convolution kernel
// to the image. Supports kernels up to 15×15.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface CustomKernelParams {
  /** Flattened kernel values (row-major). */
  kernel: number[];
  /** Kernel width (and height — must be square and odd). */
  kernelSize: number;
  /** Divisor for normalization (0 = auto-sum). */
  divisor: number;
  /** Offset added after convolution. */
  offset: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const CustomKernel: Filter<CustomKernelParams> = {
  id: "custom-kernel",
  displayName: "Custom",
  defaultParams: {
    kernel: [0, -1, 0, -1, 5, -1, 0, -1, 0],
    kernelSize: 3,
    divisor: 0,
    offset: 0,
  },

  apply(
    input: PixelBuffer,
    params: CustomKernelParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const { kernel, kernelSize, divisor, offset } = params;
    const half = Math.floor(kernelSize / 2);

    // Auto-compute divisor if not specified
    let div = divisor;
    if (div === 0) {
      let sum = 0;
      for (let i = 0; i < kernel.length; i++) {
        sum += kernel[i]!;
      }
      div = sum === 0 ? 1 : sum;
    }

    const invDiv = 1 / div;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        let r = 0, g = 0, b = 0;

        for (let ky = 0; ky < kernelSize; ky++) {
          for (let kx = 0; kx < kernelSize; kx++) {
            let sx = x + kx - half;
            let sy = y + ky - half;

            // Clamp to edge
            if (sx < 0) sx = 0;
            else if (sx >= w) sx = w - 1;
            if (sy < 0) sy = 0;
            else if (sy >= h) sy = h - 1;

            const si = (sy * w + sx) * 4;
            const kv = kernel[ky * kernelSize + kx]!;

            r += src[si]! * kv;
            g += src[si + 1]! * kv;
            b += src[si + 2]! * kv;
          }
        }

        dst[o] = clamp8(Math.round(r * invDiv + offset));
        dst[o + 1] = clamp8(Math.round(g * invDiv + offset));
        dst[o + 2] = clamp8(Math.round(b * invDiv + offset));
        dst[o + 3] = src[o + 3]!;
      }
    }

    return out;
  },
};
